import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

/**
 * Segmented control. The group is the form control (CVA) — children
 * register through DI so the group can mark the matching one active.
 *
 *   <bv-toggle-group [(value)]="viewMode">
 *     <bv-toggle value="cards">
 *       <bv-icon name="grid_view" size="sm"></bv-icon>
 *     </bv-toggle>
 *     <bv-toggle value="table">
 *       <bv-icon name="view_list" size="sm"></bv-icon>
 *     </bv-toggle>
 *   </bv-toggle-group>
 */
@Component({
    selector: "bv-toggle-group",
    template: "<ng-content></ng-content>",
    styles: [
        `
      :host {
        display: inline-flex;
        align-items: stretch;
        border: 1px solid var(--bg-separator);
        border-radius: var(--radius-md);
        overflow: hidden;
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BvToggleGroupComponent),
            multi: true,
        },
    ],
    host: {
        class: "bv-toggle-group",
        role: "group",
    }
})
export class BvToggleGroupComponent implements ControlValueAccessor {
  @Input() value: any = null;
  @Input() disabled = false;

  @Output() valueChange = new EventEmitter<any>();

  private children: BvToggleComponent[] = [];
  private onChange: (v: unknown) => void = () => {};
  private onTouched: () => void = () => {};

  register(c: BvToggleComponent): void {
    this.children.push(c);
    c.setActive(c.value === this.value);
    c.setGroupDisabled(this.disabled);
  }

  unregister(c: BvToggleComponent): void {
    this.children = this.children.filter((x) => x !== c);
  }

  selectValue(value: unknown): void {
    if (this.disabled) return;
    this.value = value;
    this.valueChange.emit(value);
    this.onChange(value);
    this.onTouched();
    this.refreshChildren();
  }

  private refreshChildren(): void {
    this.children.forEach((c) => c.setActive(c.value === this.value));
  }

  writeValue(value: unknown): void {
    this.value = value;
    this.refreshChildren();
  }
  registerOnChange(fn: (v: unknown) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.children.forEach((c) => c.setGroupDisabled(disabled));
  }
}

@Component({
    selector: "bv-toggle",
    template: "<ng-content></ng-content>",
    styles: [
        `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-1-5);
        padding: var(--space-2) var(--space-3);
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        font-weight: 500;
        color: var(--text-secondary);
        background: transparent;
        border: none;
        cursor: pointer;
        transition:
          background var(--duration-fast) var(--ease-default),
          color var(--duration-fast) var(--ease-default);
        user-select: none;
      }

      :host:focus {
        outline: none;
      }

      :host:focus-visible {
        background: var(--bg-hover);
        color: var(--text-primary);
      }

      :host(:hover) {
        background: var(--bg-hover);
        color: var(--text-primary);
      }

      :host(.bv-toggle--active) {
        background: var(--brand-subtle);
        color: var(--brand-light);
      }

      :host(.bv-toggle--active:hover) {
        background: var(--brand-subtle-hover);
      }

      :host(.bv-toggle--disabled) {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "bv-toggle",
        role: "button",
        "[attr.aria-pressed]": "active",
        "[attr.aria-disabled]": "isDisabled || null",
        "[attr.tabindex]": "isDisabled ? -1 : 0",
        "[class.bv-toggle--active]": "active",
        "[class.bv-toggle--disabled]": "isDisabled",
    }
})
export class BvToggleComponent implements OnInit, OnDestroy {
  @Input() value: unknown = null;
  @Input() disabled = false;

  active = false;
  groupDisabled = false;

  constructor(
    @Optional() private group: BvToggleGroupComponent | null,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.group?.register(this);
  }

  ngOnDestroy(): void {
    this.group?.unregister(this);
  }

  get isDisabled(): boolean {
    return this.disabled || this.groupDisabled;
  }

  setActive(active: boolean): void {
    if (this.active === active) return;
    this.active = active;
    this.cdr.markForCheck();
  }

  setGroupDisabled(disabled: boolean): void {
    this.groupDisabled = disabled;
    this.cdr.markForCheck();
  }

  @HostListener("click", ["$event"])
  onClick(event: Event): void {
    this.activate(event);
  }

  @HostListener("keydown.space", ["$event"])
  @HostListener("keydown.enter", ["$event"])
  onKeydown(event: Event): void {
    this.activate(event);
  }

  private activate(event: Event): void {
    if (this.isDisabled) return;
    event.preventDefault();
    this.group?.selectValue(this.value);
  }
}
